import React from 'react';
import { Button, mergeClasses, Spinner } from '@fluentui/react-components';
import { useData } from '@microsoft/teamsfx-react';

import useApi from 'src/hooks/useApi';
import WidgetContainer from 'src/components/molecules/widget-container/WidgetContainer';
import { PromoBanner } from 'src/models/PromoBanner';
import { LinkBehavior } from 'src/utils/enums';
import { openExternalUrl } from 'src/utils/utils';
import EnhancedButton from 'src/components/atoms/enhanced-button/EnhancedButton';
import EnhancedDialog from 'src/components/molecules/enhanced-dialog/EnhancedDialog';
import { usePromoBannerStyles } from './PromoBannerSection.styles';

type PromoBannerSectionProps = {
  className?: string
};

const PromoBannerSection: React.FC<PromoBannerSectionProps> = ({ className }) => {
  const classes = usePromoBannerStyles();
  const { api } = useApi();
  const { loading, error, data: promoBannerData } = useData<PromoBanner>(api.getPromoBanner);

  if (error) {
    throw error;
  }

  if (loading) return <Spinner />;

  if (!promoBannerData) return null;

  const { linkBehavior, linkURL, headline, subHeadline, modalHeadline, modalContent } = promoBannerData;

  const promoBanner = (
    <div data-testid="promo-banner">
      <p className={classes.headline}>{headline}</p>
      <p className={classes.subHeadline}>{subHeadline}</p>
    </div>
  );

  const renderBanner = (): React.ReactNode => {
    const behavior = (!linkBehavior && !!linkURL) ? LinkBehavior.OpenUrl : linkBehavior;

    switch (behavior) {
      case LinkBehavior.OpenUrl: {
        const handleClick = () => linkURL?.trim() && openExternalUrl(linkURL.trim());
        return (
          <EnhancedButton
            className={classes.actionElement}
            onClick={handleClick}
            isExternal={false}
            appearance="transparent"
          >
            {promoBanner}
          </EnhancedButton>
        );
      }

      case LinkBehavior.OpenTextModal: {
        return (
          <EnhancedDialog dialogTitle={modalHeadline!} dialogContent={modalContent!}>
            <Button className={classes.actionElement} appearance="transparent">{promoBanner}</Button>
          </EnhancedDialog>
        );
      }

      default: {
        return <div className={classes.actionElement}>{promoBanner}</div>;
      }
    }
  };

  return <WidgetContainer className={mergeClasses(classes.wrapper, className)}>{renderBanner()}</WidgetContainer>;
};

export default PromoBannerSection;
